import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

interface ShiftCustomFactSemiFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    schema: any;
    storages: any[];
    targetForDelete: any[];

    meta: any;
}

const state: ShiftCustomFactSemiFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    form: null,
    schema: null,

    meta: {},
};

const initState: ShiftCustomFactSemiFormState = cloneDeep(state);

export const SEMI_FORM_DATA = 'SEMI_FORM_DATA';
export const SEMI_FORM_SCHEMA = 'SEMI_FORM_SCHEMA';
export const SEMI_FORM_SAVE = 'SEMI_FORM_SAVE';
export const SEMI_PLANS_LIST = 'SEMI_PLANS_LIST';

export const actions: ActionTree<ShiftCustomFactSemiFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SEMI_FORM_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(SEMI_FORM_DATA).getContent()
                commit('SET_FORM_DATA', {
                    document: _res.document,
                    schema: _res.schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    loadShiftPlans({ commit }, { documentId }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            console.debug('[ShiftCustomFactSemiForm] load semi plans of shift', documentId);
            vm.$db([
                vm.$q(SEMI_PLANS_LIST)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'scp.id',
                            number: 'scp.number',
                            product_id: 'scp.product_id',
                        },
                        from: 's:shift_custom_plan',
                        from_as: 'scp',
                        where: {
                            $and: [
                                {$eq: [`scp.shift_id`, {$: documentId}]},
                                {$eq: [`scp.kind`, {$: 'semi'}]},
                            ]
                        },
                        order: [
                            {$asc: ['scp.number']}
                        ],
                        page_size: 100,
                    }),
            ]).then((result: ServerResult) => {
                const opResult = result.getOperation(SEMI_PLANS_LIST);
                if (!opResult.hasError()) {
                    const plans = opResult.getContent().documents;
                    commit('SET_SHIFT_PLANS', plans);
                    console.debug('[ShiftCustomFactSemiForm] loaded semi plans', plans.length);
                    resolve(plans);
                } else {
                    reject();
                }
            })
            .catch((error: any) => {
                console.log('error', error)
                reject(error);
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SEMI_FORM_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:shift_custom_fact`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(SEMI_FORM_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
        console.debug('[ShiftCustomFactSemiForm] save fact', documentId || '(new)');

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q(SEMI_FORM_SAVE)
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q(SEMI_FORM_SAVE)
                    .create('s:shift_custom_fact', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation(SEMI_FORM_SAVE);

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },
    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

};

export const mutations: MutationTree<ShiftCustomFactSemiFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    SET_SHIFT_PLANS(state, plans) {
        state.shift_plans = plans;
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ShiftCustomFactSemiFormState, {field, newValue}) {
        const schema_field = state.schema.fields.filter((item: any) => {
            return item.code == field
        })[0]

        if (schema_field && (schema_field.type == 'decimal' || schema_field.type == 'integer')) {
            newValue = parseFloat(newValue.replace(',', '.'))
        }
        switch (field) {
            case 'quantity':
                if (newValue > 600) {
                    newValue = 600;
                }
                state.form[field] = newValue;
                break

            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ShiftCustomFactSemiFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_shift_custom_fact_semi_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
